import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import type { DataTableColumn } from '@/components/common/DataTable';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { LocationDto } from '@/types/organization';
import type {
  CreateWarehouseTransferReceiptRequest,
  WarehouseTransferReceiptDto,
  WarehouseTransferReceiptLineInput,
  WarehouseTransferReceivableDto,
} from '@/types/dispatch';

/** One row to receive — one physical label when Barcode Required is on (flattened out of
 * WarehouseTransferReceivableLineDto.labels), one aggregate row per material line otherwise. */
interface ReceiveRow {
  key: string;
  dispatchOrderLineId: string;
  materialCode: string;
  materialName: string;
  maxQty: number;
  barcode: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  printedLabelId: string | null;
  productionPrintedLabelId: string | null;
  weightProductionEntryId: string | null;
}

interface RowState {
  qty: number;
  locationId: string;
  scannedBarcode: string;
  checked: boolean;
}

// Mirrors Dispatch Material Issue's own scan-driven barcode UX exactly (shared Scan Barcode field,
// scanned rows float to the top, checked is scan-driven not clickable) — the receiving half of the
// same physical unit, just posting stock IN at the destination instead of OUT at the source. The
// one addition Material Issue never needed: every row here also needs a destination Location, since
// receiving is also the putaway step for this flow (see WarehouseTransferReceipt's own doc comment
// on why there's no separate putaway pass).
export default function WarehouseTransferReceiptPage() {
  const { t } = useTranslation('dispatch');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: receivable = [] } = useQuery({
    queryKey: ['warehouse-transfer-receivable'],
    queryFn: async () => (await apiClient.get<WarehouseTransferReceivableDto[]>('/warehouse-transfer-receipts/receivable')).data,
  });
  const { data: receipts = [], isLoading: loadingReceipts } = useQuery({
    queryKey: ['warehouse-transfer-receipts'],
    queryFn: async () => (await apiClient.get<WarehouseTransferReceiptDto[]>('/warehouse-transfer-receipts')).data,
  });

  const { data: config } = useProcessConfiguration();
  const barcodeMandatory = config?.barcodeMandatory ?? false;

  const [orderId, setOrderId] = useState('');
  const selectedOrder = receivable.find((o) => o.dispatchOrderId === orderId) ?? null;

  const { data: locations = [] } = useQuery({
    queryKey: ['locations', selectedOrder?.destinationWarehouseId],
    queryFn: async () => (await apiClient.get<LocationDto[]>('/locations', { params: { warehouseId: selectedOrder!.destinationWarehouseId } })).data,
    enabled: Boolean(selectedOrder?.destinationWarehouseId),
  });
  const locationOptions = locations.filter((l) => l.status === 'Active').map((l) => ({
    value: l.id, label: l.zoneName ? `${l.zoneName} / ${l.code}` : l.code,
  }));

  const [rows, setRows] = useState<ReceiveRow[]>([]);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const [bulkLocationId, setBulkLocationId] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [scanOrder, setScanOrder] = useState<string[]>([]);

  const selectOrder = (id: string) => {
    setOrderId(id);
    setScanOrder([]);
    setBulkLocationId('');
    setScanInput('');

    const order = receivable.find((o) => o.dispatchOrderId === id);
    if (!order) {
      setRows([]);
      setRowState({});
      return;
    }

    const nextRows: ReceiveRow[] = barcodeMandatory
      ? order.lines.flatMap((line) => line.labels.map((label) => ({
          key: label.printedLabelId ?? label.productionPrintedLabelId ?? label.weightProductionEntryId ?? `${line.dispatchOrderLineId}-${label.barcode}`,
          dispatchOrderLineId: line.dispatchOrderLineId, materialCode: line.materialCode, materialName: line.materialName,
          maxQty: label.qty, barcode: label.barcode, batchNo: label.batchNo, expiryDate: label.expiryDate,
          printedLabelId: label.printedLabelId, productionPrintedLabelId: label.productionPrintedLabelId,
          weightProductionEntryId: label.weightProductionEntryId,
        })))
      : order.lines.filter((line) => line.pendingQty > 0).map((line) => ({
          key: line.dispatchOrderLineId,
          dispatchOrderLineId: line.dispatchOrderLineId, materialCode: line.materialCode, materialName: line.materialName,
          maxQty: line.pendingQty, barcode: null, batchNo: null, expiryDate: null,
          printedLabelId: null, productionPrintedLabelId: null, weightProductionEntryId: null,
        }));

    setRows(nextRows);
    setRowState(Object.fromEntries(nextRows.map((r) => [r.key, {
      qty: r.maxQty, locationId: '', scannedBarcode: '',
      // Barcode mode starts every row unconfirmed (must be scanned); without one, there's nothing
      // to scan against, so every row is already "ready".
      checked: !barcodeMandatory,
    }])));
  };

  // Refreshes the picker + resets selection whenever the underlying data changes materially (a
  // receipt was just posted, or the list reloaded) — same "selection can silently go stale"
  // guard used throughout this app's pick-list-driven screens.
  useEffect(() => {
    if (orderId && !receivable.some((o) => o.dispatchOrderId === orderId)) {
      setOrderId('');
      setRows([]);
      setRowState({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivable]);

  const tryMatchScan = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return false;
    const match = rows.find((r) => !rowState[r.key]?.checked && (r.barcode ?? '').toUpperCase() === trimmed);
    if (!match) return false;
    setRowState((prev) => ({ ...prev, [match.key]: { ...prev[match.key], checked: true, scannedBarcode: value.trim() } }));
    setScanOrder((prev) => [match.key, ...prev.filter((k) => k !== match.key)]);
    setScanInput('');
    return true;
  };

  const handleScanInputChange = (value: string) => {
    if (!tryMatchScan(value)) setScanInput(value);
  };

  const handleScanKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryMatchScan(scanInput);
    }
  };

  const orderedRows = useMemo(() => {
    if (!barcodeMandatory) return rows;
    const scannedFirst = scanOrder.map((k) => rows.find((r) => r.key === k)).filter((r): r is ReceiveRow => Boolean(r));
    const rest = rows.filter((r) => !scanOrder.includes(r.key));
    return [...scannedFirst, ...rest];
  }, [rows, scanOrder, barcodeMandatory]);

  const handleBulkLocationChange = (locationId: string) => {
    setBulkLocationId(locationId);
    setRowState((prev) => Object.fromEntries(Object.entries(prev).map(([key, s]) => [key, { ...s, locationId }])));
  };

  const scannedCount = rows.filter((r) => r.barcode && rowState[r.key]?.checked).length;
  const totalWithBarcode = rows.filter((r) => r.barcode).length;
  const allScanned = !barcodeMandatory || scannedCount === totalWithBarcode;
  const allLocated = rows.every((r) => rowState[r.key]?.qty <= 0 || Boolean(rowState[r.key]?.locationId));
  const totalToReceive = rows.reduce((sum, r) => sum + (rowState[r.key]?.qty > 0 ? rowState[r.key].qty : 0), 0);

  const receiveMutation = useMutation({
    mutationFn: () => {
      const payload: CreateWarehouseTransferReceiptRequest = {
        dispatchOrderId: orderId,
        lines: rows
          .filter((r) => (rowState[r.key]?.qty ?? 0) > 0 && rowState[r.key]?.checked)
          .map((r): WarehouseTransferReceiptLineInput => ({
            dispatchOrderLineId: r.dispatchOrderLineId, qty: rowState[r.key].qty, locationId: rowState[r.key].locationId,
            printedLabelId: r.printedLabelId, productionPrintedLabelId: r.productionPrintedLabelId,
            weightProductionEntryId: r.weightProductionEntryId,
            scannedBarcode: rowState[r.key].scannedBarcode || null,
          })),
      };
      return apiClient.post('/warehouse-transfer-receipts', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-transfer-receivable'] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-transfer-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['dispatch-orders'] });
      setOrderId('');
      setRows([]);
      setRowState({});
    },
  });

  const historyColumns: DataTableColumn<WarehouseTransferReceiptDto>[] = [
    { field: 'receiptNumber', headerName: t('receive.table.number') },
    { field: 'orderNumber', headerName: t('table.dispatchOrder'), filterable: true },
    { field: 'warehouseName', headerName: t('receive.table.destination'), filterable: true },
    { field: 'receiptDate', headerName: t('table.date'), render: (r) => formatDate(r.receiptDate) },
    { field: 'receivedByUserName', headerName: t('receive.table.receivedBy') },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
  ];

  return (
    <>
      <PageHeader title={t('receive.title')} subtitle={t('receive.subtitle')} />

      <Box sx={{ maxWidth: 520, mx: { xs: 0, sm: 'auto' } }}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <SearchableSelect
                label={t('receive.selectOrder')} value={orderId} onChange={selectOrder} fullWidth size="medium"
                helperText={t('receive.selectOrderHint')}
                options={receivable.map((o) => ({ value: o.dispatchOrderId, label: `${o.orderNumber} — ${o.sourceWarehouseName} → ${o.destinationWarehouseName}` }))}
              />

              {receivable.length === 0 && (
                <Typography variant="body2" color="text.secondary">{t('receive.nothingPending')}</Typography>
              )}

              {selectedOrder && (
                <SearchableSelect
                  label={t('receive.bulkLocation')} value={bulkLocationId} onChange={handleBulkLocationChange} fullWidth
                  helperText={t('receive.bulkLocationHelper')}
                  options={locationOptions}
                />
              )}

              {selectedOrder && barcodeMandatory && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <TextField
                    label={t('materialIssue.scanBarcode')}
                    value={scanInput}
                    onChange={(e) => handleScanInputChange(e.target.value)}
                    onKeyDown={handleScanKeyDown}
                    helperText={t('receive.scanBarcodeHelper')}
                    fullWidth
                    autoFocus
                  />
                  <Chip
                    label={t('materialIssue.scannedOfTotal', { scanned: scannedCount, total: totalWithBarcode })}
                    color={allScanned ? 'success' : 'warning'}
                    sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                  />
                </Stack>
              )}

              {selectedOrder && (
                <Stack spacing={1.5}>
                  {orderedRows.map((row) => {
                    const s = rowState[row.key];
                    const scanned = barcodeMandatory && Boolean(s?.checked) && (s?.qty ?? 0) > 0;
                    return (
                      <Box
                        key={row.key}
                        sx={{
                          p: 1.5, border: 1, borderRadius: 1,
                          borderColor: scanned ? 'success.main' : 'divider',
                          bgcolor: scanned ? 'success.50' : undefined,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                          {barcodeMandatory && <Checkbox size="small" checked={Boolean(s?.checked)} disabled sx={{ mt: -0.5, ml: -1 }} />}
                          <Box>
                            <Typography variant="subtitle2">{row.materialCode}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.materialName}</Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {row.batchNo ? `${t('pickList.batch')}: ${row.batchNo} · ` : ''}
                              {row.expiryDate ? `${t('receive.expiry')}: ${formatDate(row.expiryDate)}` : ''}
                            </Typography>
                          </Box>
                        </Stack>

                        {barcodeMandatory && (
                          <Typography variant="caption" display="block" sx={{ mb: 1.5 }} color={s?.checked ? 'success.main' : 'text.secondary'}>
                            {t('materialIssue.expectedBarcode', { barcode: row.barcode ?? '' })}
                            {s?.checked ? ` — ${t('materialIssue.scanned')}` : ''}
                          </Typography>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                          <TextField
                            type="number" label={t('receive.qtyToReceive')} size="medium" sx={{ flex: 1 }}
                            value={s?.qty ?? 0}
                            onChange={(e) => setRowState((prev) => ({ ...prev, [row.key]: { ...prev[row.key], qty: Number(e.target.value) } }))}
                            slotProps={{ htmlInput: { inputMode: 'decimal', readOnly: barcodeMandatory, max: row.maxQty } }}
                          />
                        </Stack>
                        <SearchableSelect
                          label={t('receive.location')} value={s?.locationId ?? ''}
                          onChange={(v) => setRowState((prev) => ({ ...prev, [row.key]: { ...prev[row.key], locationId: v } }))}
                          fullWidth options={locationOptions}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}

              <Divider />

              <Button
                variant="contained" size="large" fullWidth
                disabled={!orderId || totalToReceive <= 0 || !allScanned || !allLocated || receiveMutation.isPending}
                onClick={() => receiveMutation.mutate()}
              >
                {receiveMutation.isPending ? t('receive.receiving') : t('receive.receiveButton')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>{t('receive.historyTitle')}</Typography>
        <DataTable columns={historyColumns} rows={receipts} loading={loadingReceipts} searchable getRowId={(r) => r.id} exportFileName="warehouse-transfer-receipts" />
      </Box>
    </>
  );
}
